import React, { useState } from 'react';
import { OwnerProfile } from '../../../types/owner';
import { useUpdateOwnerProfileMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import {
    Box,
    Button,
    InputLabel,
    Stack,
    TextField,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const OwnerBio = ({
    owner,
    canEdit,
}: {
    owner: OwnerProfile;
    canEdit: boolean;
}) => {
    const [updateOwnerProfile, { isLoading }] = useUpdateOwnerProfileMutation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(owner.biography ?? '');

    const handleEdit = () => {
        setDraft(owner.biography ?? '');
        setEditing(true);
    };

    const handleCancel = () => {
        setDraft(owner.biography ?? '');
        setEditing(false);
    };

    const handleSave = async () => {
        try {
            await updateOwnerProfile({
                ownerId: owner.id,
                biography: draft.trim(),
            }).unwrap();
            toast.success('Bio updated successfully');
            setEditing(false);
        } catch {
            toast.error('Error updating bio');
        }
    };

    if (!canEdit) {
        return owner.biography ? (
            <Typography sx={{ pt: 2, fontStyle: 'italic' }}>
                {owner.biography}
            </Typography>
        ) : null;
    }

    if (editing) {
        return (
            <Box sx={{ pt: 2 }}>
                <InputLabel htmlFor='bio__input'>Your Bio</InputLabel>
                <TextField
                    id='bio__input'
                    placeholder='Tell guests who you are, what you specialise in or what makes your spaces different'
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                />
                <Stack
                    direction='row'
                    spacing={1}
                    sx={{ mt: 1, justifyContent: 'flex-end' }}
                >
                    <Button onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        Save
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                pt: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
            }}
        >
            <Typography
                sx={{
                    flex: 1,
                    fontStyle: 'italic',
                    color: owner.biography ? 'text.primary' : 'text.secondary',
                }}
            >
                {owner.biography || 'Add a bio to your profile'}
            </Typography>
            <Tooltip title='Edit bio'>
                <IconButton
                    size='small'
                    type='button'
                    aria-label='Edit bio'
                    onClick={handleEdit}
                >
                    <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default OwnerBio;
